import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import Swal from "sweetalert2";
import Loading from "../../../../components/Loading";
import LoadingButton from "../../../../components/Loading/LoadingButton";
import {
  getRequestByAuth,
  postRequestByAuth,
} from "../../../../function/helper";
import formEnterToTab from "./formEnterToTab";
import NewAddressDigikalaButton from "./NewAddressDigikalaButton";

export default function DigikalaSettingForm({ getSellerData }) {
  const queryClient = useQueryClient();

  const {
    isFetching: isFetchingSourceAddresses,
    data: sourceAddressesData,
    refetch,
  } = useQuery({
    queryKey: ["sourceAddressesData"],
    queryFn: () =>
      getRequestByAuth(
        `/v2/address/list?page=1&items=20&isSender=1&nationalCode=${getSellerData?.user?.nationalCode}`
      ).then(res => {
        if (res && res.status === 200) {
          return res.data;
        }

        return null;
      }),
    gcTime: 60 * 60 * 1000,
    staleTime: 60 * 60 * 1000,
    retry: 0,
    refetchOnMount: true,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      defaultAddressId: getSellerData?.seller?.defaultAddressId ?? "",
      source: getSellerData?.seller?.defaultAddress ?? {},
    },
  });

  const { isPending, mutate } = useMutation({
    mutationKey: ["storeDefaultAddress"],
    mutationFn: body =>
      postRequestByAuth("/v2/digikala/storeDefaultAddress", body).then(res => {
        if (res && res.status === 200) {
          Swal.fire({
            icon: "success",
            text: res.data.message,
            showConfirmButton: true,
            confirmButtonText: "متوجه شدم",
          });

          queryClient.refetchQueries({
            queryKey: ["DigikalaSeller"],
          });

          return;
        }

        Swal.fire({
          icon: "error",
          text: res.message,
          showConfirmButton: true,
          confirmButtonText: "متوجه شدم",
        });
      }),
  });

  const onSubmit = data => {
    if (!isPending) {
      mutate(data);
    }
  };

  useEffect(() => {
    if (!isFetchingSourceAddresses && sourceAddressesData) {
      setValue(
        "source",
        sourceAddressesData?.items?.filter(
          sourceAddress =>
            Number(sourceAddress?.id) === Number(watch("defaultAddressId"))
        )[0]
      );
    }
  }, [isFetchingSourceAddresses, sourceAddressesData]);

  if (!isFetchingSourceAddresses && !sourceAddressesData) {
    return (
      <p className="text-danger p-3 mb-0">
        در دریافت اطلاعات با خطا مواجه شدیم
      </p>
    );
  }

  return (
    <div className="mb-2 rtl">
      <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between bg-white shadow rounded px-4 py-4 text-center">
        {isFetchingSourceAddresses && <Loading className="h-25" />}

        {!isFetchingSourceAddresses && (
          <Fragment>
            <h5 className="mb-0">تنظیمات</h5>
            <NewAddressDigikalaButton onSubmit={() => refetch()} />

            <form
              onKeyDown={formEnterToTab}
              onSubmit={handleSubmit(onSubmit)}
              id="addressForm"
            >
              <div className="form-row row p-2">
                <div className="w-100 p-0">
                  <div className="form-group col-12 col-sm-6 p-1 bs">
                    <label className="pb-1">
                      <span>انتخاب آدرس پیشفرض فرستنده برای ثبت مرسولات</span>
                      <span className="text-danger">*</span>
                    </label>
                    <Controller
                      name="defaultAddressId"
                      control={control}
                      rules={{
                        required: "آدرس فرستنده پیشفرض الزامی است",
                      }}
                      render={({ field: { onChange, value }, fieldState }) => (
                        <>
                          <Form.Select
                            className="form-control"
                            value={value}
                            onChange={e => {
                              onChange(e.target.value);
                              setValue(
                                "source",
                                sourceAddressesData?.items?.filter(
                                  sourceAddress =>
                                    Number(sourceAddress?.id) ===
                                    Number(e.target.value)
                                )[0]
                              );
                            }}
                          >
                            <option value="">انتخاب کنید</option>
                            {sourceAddressesData?.items?.map(
                              (sourceAddress, i) => (
                                <option key={i} value={sourceAddress.id}>
                                  {[
                                    sourceAddress?.province?.name,
                                    sourceAddress?.city?.name,
                                    sourceAddress?.address,
                                  ]
                                    .filter(Boolean)
                                    .join(" - ")}
                                </option>
                              )
                            )}
                          </Form.Select>

                          {fieldState?.error && (
                            <div className="text-danger p-1 small">
                              {fieldState?.error?.message}
                            </div>
                          )}
                        </>
                      )}
                    />
                  </div>
                </div>

                <h5 className="mb-0 p-0 pt-4">جزئیات آدرس انتخاب شده:</h5>

                <div className="form-group col-12 col-sm-6 p-1">
                  <label className="pb-1">استان</label>
                  <Controller
                    defaultValue={""}
                    name="source.province.name"
                    control={control}
                    readOnly={true}
                    disabled={true}
                    render={({ field }) => (
                      <input
                        type="text"
                        className="form-control"
                        value={field.value}
                        disabled
                        readOnly
                      />
                    )}
                  />
                </div>

                <div className="form-group col-12 col-sm-6 p-1">
                  <label className="pb-1">شهر</label>
                  <Controller
                    defaultValue={""}
                    name="source.city.name"
                    control={control}
                    readOnly={true}
                    disabled={true}
                    render={({ field }) => (
                      <input
                        type="text"
                        className="form-control"
                        value={field.value}
                        disabled
                        readOnly
                      />
                    )}
                  />
                </div>

                <div className="form-group col-12 col-sm-6 p-1">
                  <label className="pb-1">نام</label>
                  <Controller
                    defaultValue={""}
                    name="source.name"
                    control={control}
                    readOnly={true}
                    disabled={true}
                    render={({ field }) => (
                      <input
                        type="text"
                        className="form-control"
                        value={field.value}
                        disabled
                        readOnly
                      />
                    )}
                  />
                </div>

                <div className="form-group col-12 col-sm-6 p-1">
                  <label className="pb-1">نام خانوادگی</label>
                  <Controller
                    defaultValue={""}
                    name="source.family"
                    control={control}
                    readOnly={true}
                    disabled={true}
                    render={({ field }) => (
                      <input
                        type="text"
                        className="form-control"
                        value={field.value}
                        disabled
                        readOnly
                      />
                    )}
                  />
                </div>

                <div className="form-group col-12 col-sm-6 p-1">
                  <label className="pb-1">تلفن همراه</label>
                  <Controller
                    defaultValue={""}
                    name="source.mobile"
                    control={control}
                    readOnly={true}
                    disabled={true}
                    render={({ field }) => (
                      <input
                        type="text"
                        className="form-control"
                        value={field.value}
                        disabled
                        readOnly
                      />
                    )}
                  />
                </div>

                <div className="form-group col-12 col-sm-6 p-1">
                  <label className="pb-1">کد پستی</label>
                  <Controller
                    defaultValue={""}
                    name="source.postCode"
                    control={control}
                    readOnly={true}
                    disabled={true}
                    render={({ field }) => (
                      <input
                        type="text"
                        className="form-control"
                        value={field.value}
                        disabled
                        readOnly
                      />
                    )}
                  />
                </div>

                <div className="form-group col-12 p-1">
                  <label className="pb-1">آدرس</label>
                  <Controller
                    defaultValue={""}
                    name="source.address"
                    control={control}
                    readOnly={true}
                    disabled={true}
                    render={({ field }) => (
                      <input
                        type="text"
                        className="form-control"
                        value={field.value}
                        disabled
                        readOnly
                      />
                    )}
                  />
                </div>
              </div>

              {isPending && (
                <LoadingButton
                  center
                  className="btn-success"
                  title="در حال ذخیره"
                />
              )}

              {!isPending && (
                <Button variant="success" disabled={isPending} type="submit">
                  <span> ذخیره تنظیمات</span>
                </Button>
              )}
            </form>
          </Fragment>
        )}
      </div>
    </div>
  );
}
